import { useEffect, useState } from 'react';

import { Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button';
import { ButtonAction } from '../Button/button.model';
import { Col, Row } from '../Layout';

import type { Language } from './types';

const DEFAULT_MAX_LENGTH = 200;

interface Props {
  languages?: Array<Language>;
  showModal: boolean;
  onClose: () => void;
  onCreate: (language: Language) => void | Promise<void>;
  onEdit: (language: Language) => void | Promise<void>;
  onRemove?: (language: string) => void | Promise<void>;
  availableLanguages?: Array<string>;
  langList?: Array<string>;
  defaultLang?: string;
  maxLength?: number;
  keepModalOpenOnChange?: boolean;
  saveAction?: boolean;
}

const hasValue = (value?: string) => value !== undefined && value !== null && value !== '';

function TranslatesComponent({
  languages,
  showModal,
  onClose,
  onCreate,
  onEdit,
  onRemove,
  availableLanguages,
  langList,
  defaultLang,
  maxLength = DEFAULT_MAX_LENGTH,
  keepModalOpenOnChange = false,
  saveAction = false
}: Props) {
  const [langValue, setLangValue] = useState('');
  const [langType, setLangType] = useState('');
  const [dirty, setDirty] = useState(false);
  const { t } = useTranslation();
  const languageOptions = availableLanguages ?? langList ?? [];

  const isInvalid = (checkDirty = false) => {
    const shouldValidate = checkDirty || dirty;

    if (!shouldValidate) {
      return false;
    }

    return !hasValue(langType) || langValue.length === 0 || langValue.length > maxLength;
  };

  const getDefaultLanguage = () => {
    const translationOptions = languageOptions.filter(hasValue);
    return (
      translationOptions.find((language) => language === defaultLang) ?? translationOptions[0] ?? ''
    );
  };

  const syncLanguageValue = (language: string) => {
    setLangType(language);

    const translation = languages?.find((item) => item.lang === language);
    setLangValue(translation?.value ?? '');
  };

  useEffect(() => {
    if (showModal && !dirty) {
      syncLanguageValue(langType || getDefaultLanguage());
    }
  }, [showModal, languages, languageOptions, defaultLang, dirty]);

  const handleClose = () => {
    setLangValue('');
    setLangType('');
    setDirty(false);
    onClose();
  };

  const onSubmit = async () => {
    setDirty(true);

    if (isInvalid(true) || !hasValue(langType)) {
      return;
    }

    const translation = { lang: langType, value: langValue };
    const currentLanguage = languages?.find((item) => item.lang === translation.lang);

    try {
      if (currentLanguage) {
        await onEdit(translation);
      } else {
        await onCreate(translation);
      }
    } catch {
      return;
    }

    if (keepModalOpenOnChange && currentLanguage) {
      setDirty(false);
    } else {
      setLangValue('');
      setLangType('');
      setDirty(false);
    }
  };

  const onRemoveTranslation = async () => {
    if (langType && languages?.some((language) => language.lang === langType)) {
      try {
        await onRemove?.(langType);
      } catch {
        return;
      }
      setLangValue('');
      setLangType('');
      setDirty(false);

      if (!keepModalOpenOnChange) {
        handleClose();
      }
    }
  };

  const getValidationMessage = () => {
    if (langValue.length > maxLength) {
      return t('common.validations.maxNumberValue', { max: maxLength });
    }

    if (!hasValue(langType) || langValue.length === 0) {
      return t('common.validations.fieldRequired');
    }

    return '';
  };

  return (
    <Modal show={showModal} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{t('common.label.translation')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>{t('common.label.language')}</Form.Label>
              <Form.Control
                value={langType}
                onChange={(event) => syncLanguageValue(event.target.value)}
                name="type"
                isInvalid={isInvalid()}
                as="select">
                {keepModalOpenOnChange && <option value="" hidden />}
                {languageOptions.map((language) => (
                  <option key={language} value={language}>
                    {language}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {isInvalid() ? t('common.validations.fieldRequired') : ''}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>{t('common.label.value')}</Form.Label>
              <Form.Control
                value={langValue}
                onChange={(event) => setLangValue(event.target.value)}
                type="text"
                name="value"
                as="textarea"
                rows={3}
                isInvalid={isInvalid()}
              />
              <Form.Control.Feedback type="invalid">{getValidationMessage()}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button action={ButtonAction.cancel} onClick={handleClose} />
        {onRemove && languages?.some((language) => language.lang === langType) && (
          <Button action={ButtonAction.delete} variant="danger" onClick={onRemoveTranslation} />
        )}
        <Button
          action={saveAction ? ButtonAction.save : ButtonAction.add}
          variant="primary"
          onClick={onSubmit}
        />
      </Modal.Footer>
    </Modal>
  );
}

export default TranslatesComponent;
